import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { FinancialDocumentQuery } from "@common/schemas/financialDocument/financial-document-query.schema";
import { FetchFinancialDocumentsResponse } from "@common/dto/financialDocument/fetch-financial-documents-response.dto";
import { queryFinancialDocuments } from "@lib/financialDocument";
import { keepPreviousDataOnPageChange } from "@api/keepPreviousDataOnPageChange";
import { financialDocumentQueryKeys } from "./lib";

export const FINANCIAL_DOCUMENTS_PAGE_SIZE = 100;
export const REFETCH_INTERVAL_MS = 7_000;

export interface UseFinancialDocumentsQueryParams {
    queryParams: FinancialDocumentQuery;
    shouldRefetch?: (
        data: FetchFinancialDocumentsResponse | undefined,
    ) => boolean;
    idleRefetchIntervalMs?: number;
    onSuccess?: (data: FetchFinancialDocumentsResponse) => void;
    additionalParams?: Omit<
        UseQueryOptions<FetchFinancialDocumentsResponse>,
        "queryKey" | "queryFn" | "refetchInterval"
    >;
}

export const getFinancialDocumentsQueryKey = (
    queryParams: FinancialDocumentQuery,
) =>
    queryParams.filters?.importerQueue
        ? financialDocumentQueryKeys.importerQueueWithFilters(
              queryParams.filters.importerQueue,
              queryParams,
          )
        : financialDocumentQueryKeys.list(queryParams);

export function useFinancialDocumentsQuery({
    queryParams,
    shouldRefetch,
    idleRefetchIntervalMs,
    onSuccess,
    additionalParams = {},
}: UseFinancialDocumentsQueryParams) {
    const queryKey = getFinancialDocumentsQueryKey(queryParams);

    return useQuery({
        queryKey,
        queryFn: async () => {
            const result = await queryFinancialDocuments(queryParams);
            onSuccess?.(result);
            return result;
        },
        refetchInterval(query) {
            if (shouldRefetch?.(query.state.data)) {
                return REFETCH_INTERVAL_MS;
            }
            return idleRefetchIntervalMs ?? false;
        },
        placeholderData: keepPreviousDataOnPageChange(queryKey),
        ...additionalParams,
    });
}
